import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// ---------------------------------------------------------------------------
// ANAF company-lookup API client (CLE-122).
//
// Mirrors the backend contract in backend/src/routes/anaf.ts. Used by the
// "add new client" form to pre-fill company data from a CUI. The backend
// proxies + caches ANAF; the frontend just sends the (possibly RO-prefixed)
// CUI and consumes the normalised prefill.
// ---------------------------------------------------------------------------

export interface AnafCompany {
  cui: string;
  name: string;
  regCom: string | null;
  address: string;
  postalCode: string | null;
  phone: string | null;
  caen: string | null;
  vatPayer: boolean;
  eInvoice: boolean;
  registrationStatus: string | null;
  inactive: boolean;
  /** Structured address for e-Factura (null on old cache rows). */
  street?: string | null;
  city?: string | null;
  countyName?: string | null;
  countyCode?: string | null;
}

export interface AnafClientPrefill {
  name: string;
  cifCui: string;
  regCom: string | null;
  address: string;
  phone: string | null;
  notes: string | null;
  isActive: boolean;
}

export interface AnafLookupResult {
  cui: string;
  found: boolean;
  company: AnafCompany | null;
  prefill: AnafClientPrefill | null;
  warning: string | null;
  cached: boolean;
}

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Look up a company at ANAF by CUI (with or without the "RO" prefix). The
 * backend strips the prefix, validates the digits, and returns a normalised
 * prefill. Throws ApiError on 400 (invalid CUI), 429 (rate-limited) or 502
 * (ANAF unavailable); the caller surfaces err.message to the user.
 */
export async function lookupAnafByCui(cui: string): Promise<AnafLookupResult> {
  const res = await apiFetch<Envelope<AnafLookupResult>>(
    `/api/anaf/lookup?cui=${encodeURIComponent(cui.trim())}`,
    { headers: authHeaders() },
  );
  return res.data;
}
